import * as React from "react";

import LocationAPI from "./LocationAPI";

import { Input, Form, Button, Upload, Radio } from "antd";
import { DropdownInputAnt } from "src/components/forms/updatedDropDownInputAnt";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";

import { Validation } from "src/common/utils";

import PickListContext from "src/context/pickListContext";
import { AdminPermissions } from "src/common/Permissions";
import { Spinner } from "src/components/common";
import { toast } from "react-toastify";

interface IProps {
	history: any;
	actions: any;
	match: any;
	locations?: LocationDTO[];
	location?: any;
	setLocationNameInTab: any;
}

interface IState {
	location: LocationDTO;
	loading: boolean;
	fileList: File[];
	fileUrl: string;
	parentCode: string;
	locationNames: string[];
	isLoading: boolean;
	emailValidationStatus: any;
	emailValidationMessage: string;
}

const defaultLocation: LocationDTO = {
	address1: "",
	city: "",
	countryId: undefined,
	email: "",
	id: undefined,
	isActive: true,
	name: "",
	organizationId: undefined,
	phone: "",
	provinceId: undefined,
	postalCode: "",
};

const canadaPickListId: number = 10059;
const usPickListId: number = 10214;

class LocationDetailsForm extends React.Component<IProps, IState> {
	public static contextType = PickListContext;
	constructor(props: IProps) {
		super(props);
		this.state = {
			location: defaultLocation as LocationDTO,
			loading: false,
			fileList: [],
			fileUrl: "",
			parentCode: "Canada",
			locationNames: [],
			isLoading: true,
			emailValidationStatus: undefined,
			emailValidationMessage: "",
		};
	}

	public componentDidMount() {
		const { organizationId, datasetId, locationId } = this.props.match.params;
		const { setLocationNameInTab } = this.props;

		if (locationId && Number(locationId) > 0) {
			LocationAPI.getSingleOrganizationLocation({
				id: locationId,
				organizationId,
			})
				.then((location) => {
					setLocationNameInTab(location.name);
					const newLocation = { ...location, organizationId, datasetId };
					const parentCode = this.context.pickListItems.find(
						(pItem: any) => pItem.id === location.countryId
					).code;
					this.setState((prevState) => ({
						...prevState,
						location: newLocation,
						loading: false,
						parentCode,
					}));
				})
				.then(() =>
					LocationAPI.getLocationNames({ organizationId, locationId })
				)
				.then((res) =>
					this.setState({
						locationNames: res.filter(
							(name) => name !== this.state.location.name
						),
					})
				)
				.then(() => this.setState({ isLoading: false }))
				.then(() => {
					// assume loaded email is valid
					this.setState({
						emailValidationStatus: "success",
						emailValidationMessage: "",
					});
				})
				.catch((err) => toast.error("Something went wrong"));
		} else {
			const location = defaultLocation;
			location.organizationId = organizationId;
			this.setState({ location, loading: false });
			LocationAPI.getLocationNames({ organizationId, locationId })
				.then((res) => this.setState({ locationNames: res }))
				.then(() => this.setState({ isLoading: false }))
				.then(() => {
					Validation.validateEmail(
						this.state.location.email,
						false,
						false
					).then((parameterValidation) => {
						this.setState({
							emailValidationStatus: parameterValidation.validationStatus,
							emailValidationMessage: parameterValidation.message,
						});
					});
				})
				.catch((err) => toast.error("Something went wrong"));
		}
	}

	public render() {
		const {
			location,
			fileUrl,
			locationNames,
			emailValidationStatus,
			emailValidationMessage,
		} = this.state;
		const { datasetId } = this.props.match.params;

		const citiesLabel = this.renderCitiesDropdownLabel();

		const validLocation = {
			locationName: Validation.runMultipleValidations(
				[
					Validation.validateValueIsUnique(locationNames),
					Validation.validateStringIsNotEmpty,
				],
				["This location already exists", "Please enter a name"],
				location.name
			),
			locationAddress: Validation.validateAddress(location.address1!),
			locationCountry: Validation.validateIfANumberOrNumbers(
				location.countryId!
			),
			locationProvince: Validation.validateIfANumberOrNumbers(
				location.provinceId!
			),
			locationCity: Validation.validateStringIsNotEmpty(
				location.city!,
				"Please enter in the name of a city"
			),
			locationPostalCode: Validation.validatePostalCode(
				location.postalCode!,
				this.state.location.countryId!
			),
		};

		return this.state.isLoading !== false ? (
			<Spinner />
		) : (
			<div className="container-fluid">
				<div className="border-bottom mb-4">
					<h3 className="mb-0">General</h3>
				</div>
				<div className="row">
					<div className="col-6 form-group">
						<label htmlFor="1">Location name</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={validLocation.locationName.validationStatus}
							help={validLocation.locationName.message}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Input
									id="1"
									placeholder="Enter in a location name"
									value={this.state.location.name}
									name="name"
									maxLength={50}
									onChange={this.onUpdateTextbox}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="col-6 form-group">
						<label htmlFor="isActive">Active</label>
						<Form.Item>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Radio.Group
									name="isActive"
									onChange={this.onRadioGroupChange}
									value={this.state.location.isActive}
								>
									<Radio key={`Active`} value={true}>
										Active
									</Radio>
									<Radio key={`Inactive`} value={false}>
										Inactive
									</Radio>
								</Radio.Group>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<div className="mb-4 border-bottom">
					<h1 className="h3 mb-0">Address</h1>
				</div>
				<div className="row">
					<div className="form-group col-3">
						<label htmlFor="addressLine1">Address line 1 *</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={validLocation.locationAddress.validationStatus}
							help={validLocation.locationAddress.message}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Input
									id="addressLine1"
									placeholder="Enter in an address"
									value={this.state.location.address1}
									name="address1"
									onChange={this.onUpdateTextbox}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="form-group col-3">
						<label htmlFor="addressLine2">Address line 2</label>
						<Form.Item>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Input
									id="addressLine2"
									placeholder="Enter an address"
									value={this.state.location.address2}
									name="address2"
									onChange={this.onUpdateTextbox}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="form-group col-3">
						<label htmlFor="countries">Country *</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={validLocation.locationCountry.validationStatus}
							help={validLocation.locationCountry.message}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<DropdownInputAnt
									configuration={{
										id: "CountryId",
										name: "CountryId",
										required: true,
										placeholder: "Choose a country",
										pickListCode: "Country",
										mode: "default",
									}}
									onChange={this.onCountryChange}
									pickListStore={this.context}
									value={this.state.location.countryId!}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<div className="row">
					<div className="form-group col-3">
						<label htmlFor="provinces">{citiesLabel}</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={validLocation.locationProvince.validationStatus}
							help={validLocation.locationProvince.message}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								{/* <AntDropDownInput
										pickListCode="CountryOptions"
										parentCode={this.state.parentCode}
										placeholder={this.placeholderForProvincesId()}
										onChange={this.onProvinceChange}
										value={this.state.location.provinceId}
										sort={true}
									/> */}
								<DropdownInputAnt
									configuration={{
										id: "provinceId",
										name: "provinceId",
										mode: "default",
										pickListCode: "CountryOptions",
										placeholder: this.placeholderForProvincesId(),
										required: true,
										parentCode: this.state.parentCode,
										disabled: this.state.location.countryId === undefined,
									}}
									pickListStore={this.context}
									onChange={this.onProvinceChange}
									value={this.state.location.provinceId!}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="form-group col-3">
						<label htmlFor="city">City *</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={validLocation.locationCity.validationStatus}
							help={validLocation.locationCity.message}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Input
									placeholder="Enter in a city"
									id="city"
									value={this.state.location.city}
									name="city"
									onChange={this.onUpdateTextbox}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>

					<div className="form-group col-3">
						<label htmlFor="postalCode">Postal Code *</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={validLocation.locationPostalCode.validationStatus}
							help={validLocation.locationPostalCode.message}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Input
									placeholder={
										this.state.location.countryId === usPickListId
											? "Zip Code (e.g. 12345)"
											: "Postal Code (e.g. A1B1A1)"
									}
									id="postalCode"
									value={this.state.location.postalCode}
									name="postalCode"
									onChange={this.onUpdateTextbox}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<div className="row">
					<div className="form-group col-3">
						<label htmlFor="phone">Phone</label>
						<Form.Item hasFeedback={false}>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Input
									id="phone"
									placeholder="(000) 000-0000"
									value={this.state.location.phone}
									name="phone"
									onChange={this.onUpdateTextbox}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="form-group col-3">
						<label htmlFor="fax">Fax</label>
						<Form.Item>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Input
									id="fax"
									value={this.state.location.fax}
									placeholder="(000) 000-0000"
									name="fax"
									onChange={this.onUpdateTextbox}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="form-group col-3">
						<label htmlFor="email">Email</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={emailValidationStatus}
							help={emailValidationMessage}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={
									datasetId
										? [AdminPermissions.EditDatasetLocation]
										: [AdminPermissions.EditOrganizationLocation]
								}
							>
								<Input
									id="email"
									value={this.state.location.email}
									placeholder="example@email.com"
									type="email"
									name="email"
									onChange={this.onUpdateEmail}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<div className="row">
					<div className="form-group col-3">
						<AuthorizationWrapper
							type="hide"
							permissions={
								datasetId
									? [AdminPermissions.EditDatasetLocation]
									: [AdminPermissions.EditOrganizationLocation]
							}
						>
							<Button
								type="primary"
								disabled={!this.isValid(validLocation)}
								htmlType="submit"
								loading={this.state.loading}
								onClick={this.enterLoading}
							>
								Submit
							</Button>
						</AuthorizationWrapper>
					</div>
				</div>
			</div>
		);
	}

	private placeholderForProvincesId = () => {
		const parentCode = this.state.parentCode;
		if (parentCode === "UnitedStates") {
			return "Select a state";
		} else if (parentCode === "Canada") {
			return "Select a province";
		} else {
			return "Select a place";
		}
	};

	// private modifyDefaultDropDownConfiguration = (pickListCode: string, placeholder: string , mode: string = 'default') => {
	// 	const dropDownConfig = DropDownConfiguation;
	// 	dropDownConfig.mode = mode;
	// 	dropDownConfig.name = 'CountryId';
	// 	dropDownConfig.pickListCode = pickListCode;
	// 	dropDownConfig.id = 'CountryId';
	// 	dropDownConfig.placeholder = placeholder;
	// 	dropDownConfig.required = true;
	// 	return dropDownConfig;
	// }

	private renderCitiesDropdownLabel = () => {
		const parentCode = this.state.parentCode;
		if (parentCode === "UnitedStates") {
			return "State *";
		}
		return "Province *";
	};

	private displayLocationLogoImage = () => {
		const { fileUrl } = this.state;
		return <img src={fileUrl} alt="Location logo" className="org-logo" />;
	};

	private onDropDownSearch = (e: any, options: any) => {
		const searchValue = e;
		const updatedLocation = { ...this.state.location };
		const valueThatMatchesInput = options.find((opt: any) =>
			opt.props.children.includes(searchValue)
		);

		if (valueThatMatchesInput) {
			updatedLocation.countryId = +valueThatMatchesInput.key;
			this.setState({ location: updatedLocation });
			return;
		} else {
			this.setState({ location: updatedLocation });
			return;
		}
	};

	private handleCustomUploadRequest = (options: any) => {
		const { location } = this.state;
		const { onSuccess, onError, file } = options;
		if (onError === true) {
			onError("Something went wrong");
			return;
		}
		setTimeout(() => onSuccess(this.setLocationLogo(location, file)), 0);
	};

	private setLocationLogo = (location: LocationDTO, logoFile: File) => {
		const locationWithLogoUrl = {
			...location,
			logoUrl: URL.createObjectURL(logoFile),
		};
		this.setState({
			fileList: [logoFile],
			fileUrl: URL.createObjectURL(logoFile),
			location: locationWithLogoUrl,
		});
	};

	private getLocationLogoFile = () => {
		return this.state.fileList[0];
	};

	private enterLoading = () => {
		const { location, fileList } = this.state;

		if (!fileList.length || fileList.length === 0) {
			return this.updateLocation(location);
		} else {
			return this.updateLocationWithLogo(location);
		}
	};

	private updateLocationWithLogo = (location: LocationDTO) => {
		const locationLogoFile = this.getLocationLogoFile();
		this.setState({ loading: true });
		LocationAPI.upsertLocationLogo(locationLogoFile)
			.then((logoUrl) => {
				this.setState({ location });
			})
			.then(() => this.updateLocation(location))
			.catch((err) => {
				toast.error("Error uploading logo");
				this.setState({ loading: false });
			});
	};

	private updateLocation = (location: LocationDTO) => {
		const { organizationId, locationId } = this.props.match.params;
		const toastErrorMessage = "Something went wrong";
		this.setState({ loading: true });
		LocationAPI.upsertSingleOrganizationLocation(location)
			.then((res) => this.setState({ loading: false, location: res }))
			.then(() => toast.success("Updated location"))
			.then(() => {
				if (Number(locationId) <= 0) {
					// i.e. on New Location page
					this.props.history.push(
						`/admin/organizations/${organizationId}/locations/${this.state.location.id}`
					);
				} else {
					this.props.history.push(
						`/admin/organizations/${organizationId}/locations`
					);
				}
			})
			.catch((err) => {
				toast.error(toastErrorMessage);
				this.setState({ loading: false });
			});
	};

	private onUpdateTextbox = (e: any) =>
		this.setState({
			location: { ...this.state.location, [e.target.name]: e.target.value },
		});

	private onUpdateEmail = (e: any) => {
		Validation.validateEmail(e.target.value, false, false).then(
			(parameterValidation) => {
				this.setState({
					emailValidationStatus: parameterValidation.validationStatus,
					emailValidationMessage: parameterValidation.message,
				});
			}
		);
		const location = this.state.location;
		location.email = e.target.value;
		this.setState({ location: location });
	};

	private onCountryChange = (value: any) => {
		this.checkCountry(+value.key).then(() =>
			this.setState({
				location: { ...this.state.location, countryId: +value.key },
			})
		);
	};
	private onProvinceChange = (e: any) => {
		this.setState({ location: { ...this.state.location, provinceId: e.key } });
	};

	private checkCountry = (countryId: number) => {
		const country: PickListItemResponseDTO = this.context.pickListItems.find(
			(pItem: PickListItemResponseDTO) => pItem.id! === countryId
		);
		if (country) {
			const location = { ...this.state.location, provinceId: undefined };
			this.setState({ parentCode: country.code!, location });
			return Promise.resolve();
		}

		return Promise.resolve();
	};

	private isValid = (validLoc: any) => {
		const validateValues: string[] = Object.keys(validLoc).map(
			(key) => validLoc[key].validationStatus
		);
		return Validation.isValid(validateValues);
	};

	private onRadioGroupChange = (e: any) => {
		const location = { ...this.state.location };
		location.isActive = e.target.value;
		this.setState({ location });
	};
}

export default LocationDetailsForm;
